import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {Button, Card, CardContent, Grid, TextField, Typography} from "@mui/material";
import {Timestamp, doc, getDoc, setDoc} from "firebase/firestore";
import {useLoading} from "../../components/context/LoadingContext";
import {db, eventTimingResultsCollectionName} from "../../components/Firebase/Firebase";
import CustomDatePicker from "../../components/UI/datepicker/DatePicker";

const defaultConfig = {
    ageGroupPlace: 4,
    bib: 5,
    chipTime: 11,
    city: 9,
    country: 7,
    genderPlace: 3,
    gunTime: 12,
    id: 0,
    name: 6,
    overallPlace: 2,
    pace: 13,
};

const normalizeDateField = (value) => {
    if (!value) return '';
    if (value instanceof Timestamp) {
        return value.toDate().toISOString();
    }
    if (value.toDate && typeof value.toDate === 'function') {
        return value.toDate().toISOString();
    }
    return value;
};

const toTimestamp = (value) => {
    if (!value) return null;
    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
        return null;
    }
    return Timestamp.fromDate(parsedDate);
};

const EventTimingResultPage = () => {
    const {eventId} = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useLoading(false);

    const [timingResult, setTimingResult] = useState({
        startDate: '',
        endDate: '',
        timingServiceRaceId: '',
        config: {...defaultConfig},
    });

    const configFields = useMemo(() => ([
        {name: 'id', label: 'Идентификатор'},
        {name: 'overallPlace', label: 'Место в абсолютном зачете'},
        {name: 'genderPlace', label: 'Место в гендерной группе'},
        {name: 'ageGroupPlace', label: 'Место в возрастной группе'},
        {name: 'bib', label: 'Стартовый номер'},
        {name: 'name', label: 'Имя'},
        {name: 'country', label: 'Страна'},
        {name: 'city', label: 'Город'},
        {name: 'chipTime', label: 'Время по чипу'},
        {name: 'gunTime', label: 'Время по старту дистанции'},
        {name: 'pace', label: 'Темп'},
    ]), []);

    useEffect(() => {
        const fetchTimingResult = async () => {
            setIsLoading(true);
            try {
                const docRef = doc(db, eventTimingResultsCollectionName, eventId);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setTimingResult({
                        startDate: normalizeDateField(data.startDate),
                        endDate: normalizeDateField(data.endDate),
                        timingServiceRaceId: data.timingServiceRaceId || '',
                        config: {...defaultConfig, ...(data.config || {})},
                    });
                } else {
                    setTimingResult({
                        startDate: '',
                        endDate: '',
                        timingServiceRaceId: '',
                        config: {...defaultConfig},
                    });
                }
            } catch (error) {
                console.error('Error fetching timing result', error);
            }
            setIsLoading(false);
        };

        fetchTimingResult();
    }, [eventId, setIsLoading]);

    const handleConfigChange = (field, value) => {
        const numericValue = value === '' ? '' : Number(value);
        setTimingResult(prev => ({
            ...prev,
            config: {
                ...prev.config,
                [field]: numericValue,
            }
        }));
    };

    const isConfigValid = () => Object.values(timingResult.config).every(value => value !== '' && !Number.isNaN(Number(value)));

    const isDateValid = (value) => {
        const parsed = new Date(value);
        return value && !Number.isNaN(parsed.getTime());
    };

    const isFormValid = isDateValid(timingResult.startDate)
        && isDateValid(timingResult.endDate)
        && timingResult.timingServiceRaceId
        && isConfigValid();

    const handleSave = async () => {
        if (!isFormValid) {
            return;
        }
        setIsLoading(true);
        try {
            const docRef = doc(db, eventTimingResultsCollectionName, eventId);
            const payload = {
                startDate: toTimestamp(timingResult.startDate),
                endDate: toTimestamp(timingResult.endDate),
                timingServiceRaceId: timingResult.timingServiceRaceId,
                config: Object.keys(defaultConfig).reduce((acc, key) => {
                    acc[key] = Number(timingResult.config[key]);
                    return acc;
                }, {}),
            };

            await setDoc(docRef, payload, { merge: true });
            navigate(`/events/edit/${eventId}`);
        } catch (error) {
            console.error('Error saving timing result', error);
        }
        setIsLoading(false);
    };

    const handleBack = () => {
        navigate(-1);
    };

    return (
        <div className='right-content'>
            <div className='card shadow border-0'>
                <div className="row d-flex align-items-center w-100 mb-3 px-0 mx-0">
                    <div className="col">
                        <h2 className='hd'>Настройки хронометража</h2>
                    </div>
                    <div className="col d-flex justify-content-end px-0">
                        <Button className='logInButton' variant='contained' onClick={handleBack}>Назад</Button>
                    </div>
                </div>

                <div className='form-group'>
                    <div className='row form-group'>
                        <div className='col-12 col-md-6'>
                            <CustomDatePicker
                                label="Дата начала отслеживания"
                                value={timingResult.startDate}
                                onChange={(isoDate) => setTimingResult(prev => ({...prev, startDate: isoDate}))}
                            />
                        </div>
                        <div className='col-12 col-md-6'>
                            <CustomDatePicker
                                label="Дата окончания отслеживания"
                                value={timingResult.endDate}
                                onChange={(isoDate) => setTimingResult(prev => ({...prev, endDate: isoDate}))}
                            />
                        </div>
                    </div>

                    <div className='row form-group'>
                        <div className='col-12 col-md-6'>
                            <TextField
                                className='w-100'
                                label="ID гонки в системе хронометража"
                                value={timingResult.timingServiceRaceId}
                                onChange={(e) => setTimingResult(prev => ({...prev, timingServiceRaceId: e.target.value}))}
                                sx={textFieldStyles}
                            />
                        </div>
                    </div>
                </div>

                <Card className='mt-3' sx={{backgroundColor: 'transparent', borderColor: '#CEFC4C', borderStyle: 'solid'}}>
                    <CardContent>
                        <Typography variant="h6" className='mb-3' sx={{color: '#fff'}}>
                            Настройки конфигурации
                        </Typography>
                        <Grid container spacing={2}>
                            {configFields.map(field => (
                                <Grid item xs={12} sm={6} md={4} key={field.name}>
                                    <TextField
                                        className='w-100'
                                        type="number"
                                        label={field.label}
                                        value={timingResult.config[field.name]}
                                        onChange={(e) => handleConfigChange(field.name, e.target.value)}
                                        sx={textFieldStyles}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </CardContent>
                </Card>

                <div className='d-flex justify-content-end mt-4'>
                    <Button
                        className='logInButton'
                        variant='contained'
                        onClick={handleSave}
                        disabled={!isFormValid || isLoading}
                    >
                        Сохранить
                    </Button>
                </div>
            </div>
        </div>
    );
};

const textFieldStyles = {
    '& .MuiSvgIcon-root': {
        color: '#fff',
    },
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            borderColor: '#CEFC4C',
        },
        '&:hover fieldset': {
            borderColor: '#CEFC4C',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#CEFC4C',
        },
        '& input': {
            color: '#fff',
        },
        '& textarea': {
            color: '#fff',
        },
    },
    '& .MuiInputLabel-root': {
        color: '#fff',
        '&.Mui-focused': {
            color: '#fff',
        },
    },
};

export default EventTimingResultPage;
